import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Phone, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COMPANY_WHATSAPP = '16195140452';
const COMPANY_PHONE = '+18776809272';
const SESSION_KEY = 'rbx_chat_session';
const POLL_INTERVAL_MS = 3000;

const WELCOME =
  "Hey driver — I'm Red Ball's dispatch assistant. Ask me about loads, lanes, payment, or anything else. " +
  "For instant booking, call 1 (877) 680-9272.";

export const FloatingChatbot = () => {
  const location = useLocation();
  const hidden = !location.pathname.startsWith('/load-board');

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', text: WELCOME }]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [sessionStatus, setSessionStatus] = useState('ai'); // 'ai' | 'human'
  const [assignedTo, setAssignedTo] = useState(null);
  const [loadRef, setLoadRef] = useState(null);

  // Prelude form state (shown once before first message)
  const [showPrelude, setShowPrelude] = useState(() => !localStorage.getItem(SESSION_KEY));
  const [pname, setPname] = useState('');
  const [pphone, setPphone] = useState('');
  const [pmc, setPmc] = useState('');

  const scrollRef = useRef(null);
  const lastSeenRef = useRef(null); // ISO timestamp for poll cursor

  // Auto-scroll on new messages
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Listen for "Ask about this load" button events from the LoadsTable
  useEffect(() => {
    const onOpenWithLoad = (e) => {
      const ref = e.detail?.ref;
      if (ref) {
        setLoadRef(String(ref));
        // Inject contextual welcome message
        setMessages((m) => {
          const exists = m.some((msg) => msg.text?.includes(`#${ref}`));
          if (exists) return m;
          return [
            ...m,
            { role: 'assistant', text: `I see you're asking about load #${ref}. What would you like to know?` },
          ];
        });
      }
      setOpen(true);
    };
    window.addEventListener('rbx:open-chat-with-load', onOpenWithLoad);
    return () => window.removeEventListener('rbx:open-chat-with-load', onOpenWithLoad);
  }, []);

  // Restore conversation when opening with a persisted session
  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/chat/history/${sessionId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const msgs = (data.messages || []).map(toClientMsg);
        if (msgs.length > 0) setMessages(msgs);
        if (data.session?.status) setSessionStatus(data.session.status);
        if (data.session?.assigned_to_name) setAssignedTo(data.session.assigned_to_name);
        if (msgs.length > 0) {
          lastSeenRef.current = msgs[msgs.length - 1].created_at || new Date().toISOString();
        }
      } catch (err) { console.warn('[chatbot] history fetch failed:', err); }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId]);

  // Poll for dispatcher messages every POLL_INTERVAL_MS while chat is open with an active session
  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const since = lastSeenRef.current ? `?since=${encodeURIComponent(lastSeenRef.current)}` : '';
        const res = await fetch(`${API}/chat/poll/${sessionId}${since}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (data.session?.status) setSessionStatus(data.session.status);
        setAssignedTo(data.session?.assigned_to_name || null);
        const incoming = (data.messages || []).filter((m) => m.role === 'dispatcher');
        if (incoming.length > 0) {
          // Notify user via browser + sound
          fireNotification(incoming[incoming.length - 1]);
          setMessages((m) => [...m, ...incoming.map(toClientMsg)]);
        }
        if (data.messages?.length > 0) {
          lastSeenRef.current = data.messages[data.messages.length - 1].created_at;
        }
      } catch (err) { console.warn('[chatbot] poll failed:', err); }
    };
    const id = setInterval(tick, POLL_INTERVAL_MS);
    tick();
    return () => { cancelled = true; clearInterval(id); };
  }, [open, sessionId]);

  const startSessionWithPrelude = useCallback(async () => {
    setShowPrelude(false);
    try {
      const res = await fetch(`${API}/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: pname.trim(),
          phone: pphone.trim(),
          mc: pmc.trim(),
          load_ref: loadRef || '',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
    } catch (err) { console.warn('[chatbot] start session failed:', err); }
  }, [pname, pphone, pmc, loadRef]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }, { role: 'assistant', text: '' }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId, load_ref: loadRef }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantText = '';
      let handoffToHuman = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const frame of frames) {
          const lines = frame.split('\n');
          let event = 'message';
          let data = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) event = line.slice(7).trim();
            else if (line.startsWith('data: ')) data += line.slice(6);
          }
          if (event === 'session' && data) {
            setSessionId(data);
            localStorage.setItem(SESSION_KEY, data);
          } else if (event === 'handoff' && data === 'human') {
            handoffToHuman = true;
          } else if (event === 'done') {
            // stream done
          } else if (data) {
            assistantText += data.replace(/\\n/g, '\n');
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: 'assistant', text: assistantText };
              return next;
            });
          }
        }
      }

      // If we're in human mode, replace the placeholder assistant bubble with a "waiting" hint
      if (handoffToHuman) {
        setMessages((m) => {
          const next = [...m];
          if (next.length && next[next.length - 1].role === 'assistant' && !next[next.length - 1].text) {
            next[next.length - 1] = {
              role: 'system',
              text: '⏳ A live dispatcher will reply shortly. Hang tight…',
            };
          } else if (next.length && next[next.length - 1].role === 'assistant' && !assistantText) {
            next.pop();
            next.push({ role: 'system', text: '⏳ A live dispatcher will reply shortly. Hang tight…' });
          }
          return next;
        });
      } else if (!assistantText) {
        // Drop empty placeholder
        setMessages((m) => {
          const next = [...m];
          if (next.length && next[next.length - 1].role === 'assistant' && !next[next.length - 1].text) {
            next.pop();
          }
          return next;
        });
      }
    } catch (err) {
      console.error('[chatbot] sendMessage failed:', err);
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: 'assistant',
          text: 'Sorry — something went wrong. Call dispatch at 1 (877) 680-9272 for immediate help.',
        };
        return next;
      });
    } finally {
      setStreaming(false);
      lastSeenRef.current = new Date().toISOString();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (hidden) return null;

  const waMsg = encodeURIComponent(
    loadRef ? `Hi, I'm interested in load #${loadRef}.` : "Hi, I'd like to know more about your loads."
  );

  const isHuman = sessionStatus === 'human';

  return (
    <>
      {/* WhatsApp floating button */}
      <a
        href={`https://wa.me/${COMPANY_WHATSAPP}?text=${waMsg}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 right-6 z-40 w-14 h-14 bg-[#25d366] hover:bg-[#1ebe57] text-white flex items-center justify-center shadow-2xl rounded-full transition-transform hover:scale-110"
        data-testid="floating-whatsapp"
        aria-label="Chat on WhatsApp"
        title="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
          <path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.2-.7.2-.2.3-.8.9-1 1.1-.2.2-.4.2-.6.1-.3-.2-1.2-.4-2.3-1.4-.8-.7-1.4-1.6-1.6-1.9-.1-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4 0 1.4 1 2.8 1.2 3 .1.2 2 3 4.7 4.1.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3z"/>
          <path d="M12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.4c1.4.8 3.1 1.2 4.8 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3.2.8.9-3.1-.2-.3c-.9-1.3-1.3-2.9-1.3-4.4 0-4.5 3.7-8.2 8.2-8.2s8.2 3.7 8.2 8.2c.1 4.6-3.6 8.4-8 8.4z"/>
        </svg>
      </a>

      {/* Chatbot toggle */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 ${open ? 'bg-zinc-900' : 'bg-red-600 hover:bg-red-700'} text-white flex items-center justify-center shadow-2xl rounded-full transition-transform hover:scale-110`}
        data-testid="chatbot-toggle"
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)] h-[560px] max-h-[calc(100vh-8rem)] bg-white shadow-2xl border border-zinc-200 flex flex-col"
          data-testid="chatbot-panel"
        >
          {/* Header */}
          <div className={`px-4 py-3 flex items-center justify-between ${isHuman ? 'bg-green-700' : 'bg-zinc-900'} text-white`}>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <div>
                <div className="font-black uppercase tracking-wider text-sm" data-testid="chatbot-header-title">
                  {isHuman ? `${assignedTo || 'Dispatcher'} · Live` : 'Dispatch AI'}
                </div>
                <div className="text-[10px] text-white/70">
                  {isHuman ? 'A real dispatcher is on the chat' : 'Usually replies instantly'}
                </div>
              </div>
            </div>
            <a
              href={`tel:${COMPANY_PHONE}`}
              className="bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 flex items-center gap-1"
              data-testid="chatbot-call-cta"
            >
              <Phone className="w-3 h-3" />
              Call
            </a>
          </div>

          {/* Prelude form (one-time) */}
          {showPrelude ? (
            <div className="flex-1 overflow-y-auto px-4 py-4 bg-zinc-50" data-testid="chatbot-prelude">
              <div className="bg-white border border-zinc-200 p-3 mb-3 text-xs text-zinc-600 leading-relaxed">
                Tell us a bit about you so dispatch can help faster. <strong>All optional</strong> — feel free to skip.
              </div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Your Name</label>
              <input
                type="text"
                value={pname}
                onChange={(e) => setPname(e.target.value)}
                placeholder="Joe Carrier"
                className="w-full bg-white border border-zinc-200 text-sm px-3 py-2 mb-2 focus:outline-none focus:border-red-600"
                data-testid="prelude-name"
              />
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Phone</label>
              <input
                type="tel"
                value={pphone}
                onChange={(e) => setPphone(e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full bg-white border border-zinc-200 text-sm px-3 py-2 mb-2 focus:outline-none focus:border-red-600"
                data-testid="prelude-phone"
              />
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">MC Number</label>
              <input
                type="text"
                value={pmc}
                onChange={(e) => setPmc(e.target.value)}
                placeholder="MC#"
                className="w-full bg-white border border-zinc-200 text-sm px-3 py-2 mb-3 focus:outline-none focus:border-red-600"
                data-testid="prelude-mc"
              />
              <button
                onClick={startSessionWithPrelude}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider text-sm py-2.5"
                data-testid="prelude-continue"
              >
                Start Chatting →
              </button>
              <button
                onClick={() => setShowPrelude(false)}
                className="w-full mt-2 text-zinc-500 hover:text-zinc-900 text-xs underline underline-offset-2"
                data-testid="prelude-skip"
              >
                Skip — just take me to chat
              </button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-zinc-50">
                {messages.map((m, i) => (
                  <MessageBubble
                    key={m.id || `${m.role}-${m.created_at || ''}-${i}`}
                    msg={m}
                    streaming={streaming && i === messages.length - 1}
                  />
                ))}
              </div>

              {/* Input */}
              <div className="border-t border-zinc-200 p-2 flex gap-2 bg-white">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={isHuman ? "Type to chat with dispatcher…" : "Ask about loads, payment, lanes…"}
                  disabled={streaming}
                  className="flex-1 bg-zinc-50 border border-zinc-200 text-sm px-3 py-2 focus:outline-none focus:border-red-600 placeholder:text-zinc-400 disabled:opacity-60"
                  data-testid="chatbot-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || streaming}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-zinc-300 text-white px-3 flex items-center justify-center"
                  data-testid="chatbot-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

const MessageBubble = ({ msg, streaming }) => {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed bg-red-600 text-white">
          {msg.text}
        </div>
      </div>
    );
  }
  if (msg.role === 'dispatcher') {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-green-700 mb-0.5 flex items-center gap-1">
            <User className="w-3 h-3" />
            {msg.sender_name || 'Dispatcher'}
          </div>
          <div className="px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed bg-green-50 border border-green-200 text-zinc-900">
            {msg.text}
          </div>
        </div>
      </div>
    );
  }
  if (msg.role === 'system') {
    return (
      <div className="flex justify-center">
        <div className="text-[11px] text-zinc-500 italic px-3 py-1">{msg.text}</div>
      </div>
    );
  }
  // assistant
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed bg-white border border-zinc-200 text-zinc-900">
        {msg.text || (streaming ? (
          <span className="inline-flex gap-1">
            <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </span>
        ) : '')}
      </div>
    </div>
  );
};

// ===== helpers =====
const toClientMsg = (m) => ({
  role: m.role,
  text: m.text,
  sender_name: m.sender_name,
  created_at: m.created_at,
});

const fireNotification = (msg) => {
  // Visual flash via window title
  const orig = document.title;
  document.title = '💬 New message from dispatcher';
  setTimeout(() => { document.title = orig; }, 5000);
  // Subtle audio chime (won't crash if blocked)
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.05, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      o.start();
      o.stop(ctx.currentTime + 0.25);
    }
  } catch (err) { console.debug('[chatbot] audio chime blocked:', err); }
};

export default FloatingChatbot;
